/**
 * TabOverflowMenu — the "Mehr" overflow dropdown for the tab bar.
 * Hand-rolled: useState, no new deps.
 * When the active tab is one of the overflow tabs, shows its label on the trigger.
 * [Source: v0 kunden-view.tsx DropdownMenu "Mehr" trigger logic]
 */
import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Badge } from '../../../components/ui/Badge';
import { cn } from '../../../lib/cn';

export interface OverflowTab {
  value: string;
  label: string;
  count?: number;
}

interface TabOverflowMenuProps {
  tabs: OverflowTab[];
  activeTab: string;
  onSelect: (value: string) => void;
}

export function TabOverflowMenu({ tabs, activeTab, onSelect }: TabOverflowMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const activeOverflow = tabs.find((t) => t.value === activeTab);

  useEffect(() => {
    if (!open) return;
    function onMouseDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (tabs.length === 0) return null;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          'inline-flex h-[calc(100%-2px)] flex-none items-center justify-center gap-1 rounded px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          activeOverflow
            ? 'bg-background text-foreground shadow-xs'
            : 'text-muted-foreground hover:text-foreground',
        )}
      >
        {activeOverflow ? activeOverflow.label : 'Mehr'}
        <ChevronDown className="size-3.5" />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1 min-w-44 rounded-md border bg-popover p-1 shadow-md">
          {tabs.map((t) => (
            <button
              key={t.value}
              type="button"
              role="option"
              aria-selected={activeTab === t.value}
              onClick={() => {
                onSelect(t.value);
                setOpen(false);
              }}
              className={cn(
                'flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-xs font-medium transition-colors hover:bg-accent hover:text-accent-foreground',
                activeTab === t.value && 'bg-accent text-accent-foreground',
              )}
            >
              <span className="flex-1 text-left">{t.label}</span>
              {t.count !== undefined && (
                <Badge variant="neutral" className="text-[10px]">
                  {t.count}
                </Badge>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
